import React, { useState, useEffect } from 'react';
import { Button, Drawer, Tree, Spin, Badge, Space, Typography, Input, message } from 'antd';
import { 
  MenuOutlined, 
  EnvironmentOutlined, 
  DesktopOutlined, 
  SendOutlined,
  LogoutOutlined,
  UserOutlined,
  TeamOutlined,
  MailOutlined,
  LockOutlined,
  CloseOutlined,
  SaveOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { 
  LayoutDashboard, 
  PlayCircle, 
  FolderOpen, 
  Monitor, 
  ChevronDown, 
  ChevronRight,
  PlusSquare,
  Server,
  Cpu,
  Video,
  Clapperboard
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import logoTop from './logo_top.png';

const { Text } = Typography;

const MobileLayout = ({ children, title }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [treeData, setTreeData] = useState([]);
  const [unregisteredCount, setUnregisteredCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isBoxesOpen, setIsBoxesOpen] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState([]);
  
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [userStr] = useState(localStorage.getItem('user'));
  const user = userStr ? JSON.parse(userStr) : { username: 'User', perms: {} };

  const [formData, setFormData] = useState({
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    email: user.email || '',
    password: ''
  });

  const navigate = useNavigate();
  const location = useLocation();

  const displayName = (user.first_name || user.last_name) 
    ? `${user.first_name || ''} ${user.last_name || ''}`.trim() 
    : user.username;

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    window.location.href = '/login';
  };

  const handleSaveProfile = async () => {
    setProfileLoading(true);
    try {
      await axios.put(`/api/users/manage/`, {
        id: user.id,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        password: formData.password
      });
      message.success('Profile updated');
      const updatedUser = { ...user, ...formData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setIsOverlayOpen(false);
      window.location.reload();
    } catch (error) {
      message.error(error.response?.data?.error || 'Update failed');
    } finally {
      setProfileLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const resList = await axios.get('/api/box/list/');
      const filteredBoxes = resList.data.filter(box => box.id !== 294);
      const locations = {};
      filteredBoxes.forEach(box => {
        const locName = box.location || 'Unknown';
        if (!locations[locName]) locations[locName] = [];
        locations[locName].push(box);
      });
      const formattedTree = Object.keys(locations).map((loc, index) => ({
        title: loc.toUpperCase(),
        key: `loc-${index}`,
        selectable: false,
        icon: <EnvironmentOutlined style={{ color: '#60a5fa', fontSize: '14px' }} />,
        children: locations[loc].map(box => ({
          title: box.box_name || box.box_id,
          key: box.id.toString(),
          icon: <DesktopOutlined style={{ color: box.status === 'online' ? '#34d399' : '#ef4444', fontSize: '14px' }} />,
          isLeaf: true
        }))
      }));
      setTreeData(formattedTree);
      const resUnreg = await axios.get('/api/box/registration-data/');
      setUnregisteredCount(resUnreg.data.unregistered.length);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const navTo = (path) => {
    navigate(path);
    setIsDrawerOpen(false);
  };

  const onSelect = (selectedKeys, info) => {
    if (info.node.isLeaf) {
      navigate(`/screendetails/${info.node.key}`);
      setIsDrawerOpen(false);
    }
  };

  const isInsideBox = location.pathname.startsWith('/screendetails/');
  const currentBoxId = location.pathname.split('/').pop();

  // STAVKE MENIJA SA NOVIM PERMISIJAMA
  const menuItems = [
    { name: 'Dashboard', path: '/', icon: <LayoutDashboard size={18} />, show: user.perms?.dashboard || user.is_superuser },
    { name: 'Media Library', path: '/media', icon: <FolderOpen size={18} />, show: user.perms?.media || user.is_superuser },
    { name: 'Video Editor', path: '/video-editor', icon: <Video size={18} />, show: user.perms?.video_editor || user.is_superuser },
    { name: 'Animation Editor', path: '/animation-editor', icon: <Clapperboard size={18} />, show: user.perms?.animation_editor || user.is_superuser },
    { name: 'Playlists', path: '/playlists', icon: <PlayCircle size={18} />, show: user.perms?.playlists || user.is_superuser },
    { name: 'Register Box', path: '/registration', icon: <PlusSquare size={18} />, show: user.perms?.machines || user.is_superuser, isRegistration: true },
    { name: 'Register Machine', path: '/register-machine', icon: <Cpu size={18} />, show: user.perms?.machines || user.is_superuser },
    { name: 'Machine Mappings', path: '/mappings', icon: <Server size={18} />, show: user.perms?.mappings || user.is_superuser },
    
    // SADA PROVERAVA PERMISIJU box_management
    { name: 'Box Management', path: '/box-management', icon: <SettingOutlined style={{ fontSize: '18px' }} />, show: user.perms?.box_management || user.is_superuser },
    
    { name: 'Telegram Users', path: '/telegram-users', icon: <SendOutlined style={{ fontSize: '18px' }} />, show: user.perms?.telegram || user.is_superuser },
    { name: 'User Management', path: '/users', icon: <TeamOutlined style={{ fontSize: '18px' }} />, show: user.perms?.users || user.is_superuser },
  ];

  return (
    <div style={{ background: '#0f172a', minHeight: '100vh' }}>
      <div style={{ height: '60px', background: '#1e293b', display: 'flex', alignItems: 'center', padding: '0 15px', borderBottom: '1px solid #334155', position: 'sticky', top: 0, zIndex: 100 }}>
        <Button icon={<MenuOutlined />} onClick={() => setIsDrawerOpen(true)} style={{ background: 'transparent', color: 'white', border: 'none', fontSize: '20px' }} />
        
        <div style={{ color: 'white', fontWeight: 'bold', marginLeft: '5px', textTransform: 'uppercase', fontSize: '12px', flex: 1, letterSpacing: '0.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {title}
        </div>

        <Space size="middle" style={{ marginLeft: '10px' }}>
          <Space size={5} style={{ cursor: 'pointer' }} onClick={() => setIsOverlayOpen(true)}>
            <UserOutlined style={{ color: '#3b82f6', fontSize: '14px' }} />
            <Text style={{ color: 'white', fontSize: '12px', fontWeight: '500' }}>{displayName}</Text>
          </Space>
          <Button 
            type="text" 
            icon={<LogoutOutlined style={{ color: '#ef4444', fontSize: '18px' }} />} 
            onClick={handleLogout}
            style={{ padding: '4px' }}
          />
        </Space>
      </div>

      <Drawer 
        placement="left" 
        onClose={() => setIsDrawerOpen(false)} 
        open={isDrawerOpen} 
        styles={{ body: { background: '#1e293b', padding: '15px 0' } }} 
        width={260} 
        closable={false}
      >
        <div style={{ textAlign: 'center', margin: '10px 0 30px 0' }}>
            <img src={logoTop} alt="Logo" style={{ maxHeight: '35px', maxWidth: '100%' }} />
        </div>
        
        <nav style={{ flex: 1 }}>
          <ul style={{ listStyle: 'none', padding: '0', margin: 0 }}>
            {menuItems.filter(i => i.show).map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <li key={item.name} onClick={() => navTo(item.path)} className={`nav-li ${isActive ? 'active' : ''}`}>
                  {isActive && <div className="active-line-left" />}
                  <span className="nav-icon" style={{ 
                    color: isActive ? '#60a5fa' : (item.isRegistration && unregisteredCount > 0 ? '#fbbf24' : '#64748b'),
                    filter: (item.isRegistration && unregisteredCount > 0) ? 'drop-shadow(0 0 5px #fbbf24)' : 'none'
                  }}>
                    {item.icon}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    {item.name}
                    {item.isRegistration && unregisteredCount > 0 && <Badge count={unregisteredCount} size="small" style={{ backgroundColor: '#ef4444' }} />}
                  </div>
                </li>
              );
            })}

            <li onClick={() => setIsBoxesOpen(!isBoxesOpen)} className={`nav-li ${(isBoxesOpen || isInsideBox) ? 'active-no-bg' : ''}`}>
              {(isBoxesOpen || isInsideBox) && <div className="active-line-left" />}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span className="nav-icon" style={{ color: (isBoxesOpen || isInsideBox) ? '#60a5fa' : '#64748b' }}><Monitor size={18} /></span>
                  Boxes
                </div>
                {isBoxesOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </div>
            </li>
          </ul>

          {isBoxesOpen && (
            <div style={{ padding: '5px 10px 15px 15px', background: 'rgba(15, 23, 42, 0.3)' }}>
              {loading ? <Spin size="small" style={{ marginLeft: '25px' }} /> : (
                <Tree
                  showIcon blockNode onSelect={onSelect} onExpand={(keys) => setExpandedKeys(keys)}
                  expandedKeys={expandedKeys} expandAction="click" treeData={treeData}
                  style={{ background: 'transparent', color: '#94a3b8', fontSize: '0.85rem' }}
                  className="sidebar-tree"
                  selectedKeys={[currentBoxId]}
                />
              )}
            </div>
          )}
        </nav>
      </Drawer>

      <div style={{ padding: '10px' }}>{children}</div>

      {isOverlayOpen && (
        <div className="m-custom-overlay">
          <div className="m-overlay-content">
            <div className="m-overlay-header">
              <span>MY PROFILE</span>
              <Button type="text" icon={<CloseOutlined style={{ color: 'white' }} />} onClick={() => setIsOverlayOpen(false)} />
            </div>
            <div className="m-overlay-body">
              <Spin spinning={profileLoading}>
                <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                   <Text style={{ color: '#64748b', fontSize: '10px' }}>USERNAME: </Text>
                   <Text style={{ color: '#3b82f6', fontWeight: 'bold' }}>{user.username.toUpperCase()}</Text>
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label className="m-label">FIRST NAME</label>
                  <Input 
                    value={formData.first_name} 
                    onChange={e => setFormData({...formData, first_name: e.target.value})}
                    style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }}
                  />
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <label className="m-label">LAST NAME</label>
                  <Input 
                    value={formData.last_name} 
                    onChange={e => setFormData({...formData, last_name: e.target.value})}
                    style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }}
                  />
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <label className="m-label">EMAIL</label>
                  <Input 
                    prefix={<MailOutlined style={{ color: '#64748b' }} />}
                    value={formData.email} 
                    onChange={e => setFormData({...formData, email: e.target.value})}
                    style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label className="m-label">NEW PASSWORD (OPTIONAL)</label>
                  <Input.Password 
                    prefix={<LockOutlined style={{ color: '#64748b' }} />}
                    value={formData.password} 
                    onChange={e => setFormData({...formData, password: e.target.value})}
                    style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }}
                  />
                </div>

                <Button block type="primary" icon={<SaveOutlined />} onClick={handleSaveProfile} style={{ background: '#3b82f6', height: '45px', fontWeight: 'bold' }}>
                  SAVE CHANGES
                </Button>
              </Spin>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .m-custom-overlay { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(0,0,0,0.85); z-index: 9999; display: flex; align-items: center; justify-content: center; }
        .m-overlay-content { background: #1e293b; width: 85%; max-width: 400px; border-radius: 12px; border: 1px solid #334155; overflow: hidden; }
        .m-overlay-header { padding: 15px; border-bottom: 1px solid #334155; display: flex; justify-content: space-between; align-items: center; color: white; font-weight: bold; font-size: 14px; }
        .m-overlay-body { padding: 20px; }
        .m-label { color: #64748b; font-size: 10px; font-weight: bold; display: block; margin-bottom: 5px; }

        .nav-li { margin-bottom: 4px; display: flex; align-items: center; padding: 12px 20px; cursor: pointer; font-size: 0.9rem; color: #94a3b8; transition: all 0.2s; position: relative; }
        .nav-li.active { background: rgba(59, 130, 246, 0.1); color: white; }
        .nav-li.active-no-bg { color: white; }
        .nav-icon { margin-right: 12px; display: flex; }
        .active-line-left { position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: #60a5fa; box-shadow: 0 0 10px #60a5fa; z-index: 10; }
        .sidebar-tree .ant-tree-node-content-wrapper { color: #94a3b8 !important; padding: 4px 4px !important; transition: 0.2s; position: relative; }
        .ant-tree { background: transparent !important; }
      `}</style>
    </div>
  );
};

export default MobileLayout;